/*
Copyright 2020 Google LLC

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    https://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

// HTGAA addition: configure a wheel from URL query parameters, so the
// wheel can be embedded in a page with per-embed configuration, e.g.:
//
//   <iframe src="/wheel/?entries=Amina,Bosco,Carla&title=Presenters&display=1">
//
// The URL-configured wheel skips the localStorage restore, so every embed
// gets exactly the wheel described by its URL.
import WheelConfig from './WheelConfig.js';

const BOOL_FIELDS = [
  'playCheers', 'launchConfetti', 'animateWinner', 'autoRemoveWinner',
  'displayWinnerDialog', 'displayRemoveButton', 'displayHideButton',
  'allowDuplicates', 'slowSpin', 'drawOutlines', 'showTitle',
];
const NUMBER_FIELDS = {
  spinTime: [1, 120],
  maxNames: [10, 1000],
  duringSpinSoundVolume: [0, 100],
  afterSpinSoundVolume: [0, 100],
};
const STRING_FIELDS = [
  'title', 'description', 'winnerMessage', 'centerText', 'hubSize',
  'duringSpinSound', 'afterSpinSound',
];

function parseBool(v) {
  return ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
}

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

export function getUrlWheelConfig(winnerMessage) {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (ex) {
    return null;
  }
  const hasEntries = params.has('entries');
  const hasNumbered = params.has('numbered');
  if (!hasEntries && !hasNumbered) return null;

  const cfg = new WheelConfig(winnerMessage);
  if (hasNumbered) {
    // Anonymous/numbered mode: the entry list is simply 1..N, so no
    // student names ever appear in a public URL or repo history.
    const n = clamp(parseInt(params.get('numbered'), 10) || 0, 2, 100);
    cfg.entries = Array.from({ length: n }, (_, i) => ({ text: String(i + 1) }));
  } else {
    cfg.entries = params.get('entries')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
      .slice(0, 1000)
      .map(text => ({ text }));
    if (cfg.entries.length < 2) return null;
  }
  for (const [field, [lo, hi]] of Object.entries(NUMBER_FIELDS)) {
    if (params.has(field)) {
      const n = Number(params.get(field));
      if (!Number.isNaN(n)) cfg[field] = clamp(n, lo, hi);
    }
  }
  for (const field of STRING_FIELDS) {
    if (params.has(field)) cfg[field] = params.get(field);
  }
  for (const field of BOOL_FIELDS) {
    if (params.has(field)) cfg[field] = parseBool(params.get(field));
  }
  if (params.has('pageBackgroundColor')) {
    // The app's canonical form is '#RRGGBB' (its color dialog emits that,
    // and WheelPainter feeds the value straight to canvas fillStyle — an
    // invalid color there is silently ignored and paints the canvas BLACK).
    // Accept an optional '#' since a raw '#' in a URL starts the fragment
    // and truncates the whole query string; invalid values fall back to the
    // default rather than producing a black canvas.
    const bg = params.get('pageBackgroundColor').trim();
    const m = bg.match(/^#?([0-9a-fA-F]{6})$/);
    if (m) cfg.pageBackgroundColor = '#' + m[1];
  }
  if (params.has('colors')) {
    const colors = params.get('colors')
      .split(',')
      .map(c => c.trim().replace(/^#?/, '#'))
      .filter(c => /^#[0-9a-fA-F]{6}$/.test(c));
    if (colors.length) cfg.setColorValues(colors);
  }
  return cfg;
}

// display=1 switches the page into a presentation mode: the toolbar, the
// editor column and the info cards are hidden via CSS so an embedded wheel
// shows nothing but title, wheel and winner dialog.
export function isDisplayMode() {
  try {
    return parseBool(new URLSearchParams(window.location.search).get('display') || '');
  } catch (ex) {
    return false;
  }
}
